/**
 * Prescription Scanner Service
 * 
 * Uses device camera to capture prescription labels and AI vision
 * to extract medication details automatically.
 * 
 * Features:
 * - Camera capture via Capacitor
 * - AI-powered text extraction
 * - Smart field parsing for medications
 * - Web fallback with file upload
 */

import { Capacitor } from '@capacitor/core';
import API from '@/utils/api';

// Dynamic import for Camera plugin
let Camera = null;
let CameraResultType = null;
let CameraSource = null;

/**
 * Initialize Camera plugin
 */
const initCameraPlugin = async () => {
  if (Camera) return true;
  
  try {
    const module = await import('@capacitor/camera');
    Camera = module.Camera;
    CameraResultType = module.CameraResultType;
    CameraSource = module.CameraSource;
    return true;
  } catch (error) {
    console.error('[Scanner] Failed to load Camera plugin:', error);
    return false;
  }
};

/**
 * Check if camera is available
 */
export const isCameraAvailable = async () => {
  if (!Capacitor.isNativePlatform()) {
    // Web can still use file input
    return { available: true, native: false };
  }
  
  const isAvailable = await initCameraPlugin();
  return { available: isAvailable, native: isAvailable };
};

/**
 * Capture image from camera
 * 
 * @returns {Object} - { base64, format, width, height }
 */
export const captureImage = async () => {
  const { available, native } = await isCameraAvailable();
  
  if (!available) {
    throw new Error('Camera not available');
  }
  
  if (native && Camera) {
    try {
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Base64,
        source: CameraSource.Camera,
        width: 1920,
        height: 1080,
        correctOrientation: true,
      });
      
      return {
        base64: image.base64String,
        format: image.format || 'jpeg',
        dataUrl: `data:image/${image.format || 'jpeg'};base64,${image.base64String}`,
      };
    } catch (error) {
      if (error.message?.includes('cancelled') || error.message?.includes('User cancelled')) {
        return { cancelled: true };
      }
      throw error;
    }
  }
  
  // Web fallback - this will be handled by a file input in the UI
  return { needsFileInput: true };
};

/**
 * Select image from gallery
 */
export const selectFromGallery = async () => {
  const { available, native } = await isCameraAvailable();
  
  if (native && Camera) {
    try {
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Base64,
        source: CameraSource.Photos,
        width: 1920,
        height: 1080,
      });
      
      return {
        base64: image.base64String,
        format: image.format || 'jpeg',
        dataUrl: `data:image/${image.format || 'jpeg'};base64,${image.base64String}`,
      };
    } catch (error) {
      if (error.message?.includes('cancelled') || error.message?.includes('User cancelled')) {
        return { cancelled: true };
      }
      throw error;
    }
  }
  
  return { needsFileInput: true };
};

/**
 * Convert File to base64
 */
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      resolve({
        base64,
        format: file.type.split('/')[1] || 'jpeg',
        dataUrl: reader.result,
      });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Extract medication details from prescription image using AI
 * 
 * @param {string} base64Image - Base64 encoded image
 * @returns {Object} - Extracted medication details
 */
export const extractMedicationDetails = async (base64Image) => {
  try {
    const response = await API.post('/medical/scan-prescription', {
      image_base64: base64Image,
    });
    
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error('[Scanner] Extraction failed:', error);
    return {
      success: false,
      error: error.response?.data?.detail || error.message || 'Failed to extract medication details',
    };
  }
};

/**
 * Validate extracted data
 */
export const validateExtractedData = (data) => {
  const issues = [];
  
  if (!data.name) {
    issues.push('Medication name not detected');
  }
  
  if (!data.dosage && !data.strength) {
    issues.push('Dosage/strength not detected');
  }
  
  if (!data.frequency && !data.directions) {
    issues.push('Frequency/directions not detected');
  }
  
  return {
    isValid: issues.length === 0,
    issues,
    confidence: data.confidence || 'medium',
  };
};

/**
 * Format extracted data for medication form
 */
export const formatForMedicationForm = (extractedData) => {
  return {
    name: extractedData.name || '',
    dosage: extractedData.dosage || extractedData.strength || '',
    frequency: extractedData.frequency || '',
    route: extractedData.route || '',
    prescribed_for: extractedData.prescribed_for || extractedData.indication || '',
    instructions: extractedData.directions || extractedData.instructions || '',
    prescribing_doctor: extractedData.prescriber || extractedData.doctor || '',
    pharmacy: extractedData.pharmacy || '',
    pharmacy_phone: extractedData.pharmacy_phone || '',
    quantity_prescribed: extractedData.quantity ? parseInt(extractedData.quantity, 10) : null,
    refills_remaining: extractedData.refills != null ? parseInt(extractedData.refills, 10) : null,
    days_supply: extractedData.days_supply ? parseInt(extractedData.days_supply, 10) : null,
    start_date: extractedData.fill_date || new Date().toISOString().split('T')[0],
  };
};

export default {
  isCameraAvailable,
  captureImage,
  selectFromGallery,
  fileToBase64,
  extractMedicationDetails,
  validateExtractedData,
  formatForMedicationForm,
};
